import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Pencil, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import AddStudentDialog from "@/components/AddStudentDialog";
import { api } from "@/api";
import { formatINR, RiskBadge, initials, riskStyle } from "@/lib/risk";

export default function Students() {
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  const [course, setCourse] = useState("All");
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const nav = useNavigate();

  const load = () => api.students().then(setStudents).catch(() => {});
  useEffect(() => { load(); }, []);

  const courses = useMemo(
    () => ["All", ...Array.from(new Set(students.map((s) => s.course)))],
    [students]
  );

  const rows = useMemo(() => {
    let r = students;
    if (course !== "All") r = r.filter((s) => s.course === course);
    if (q.trim()) r = r.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));
    return r;
  }, [students, q, course]);

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink">Students</h1>
          <p className="text-ink-muted">{students.length} students at BrightPath Academy.</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="students-add-btn"
          className="h-10 gap-1.5 rounded-xl bg-indigo-brand font-semibold hover:bg-indigo-deep">
          <Plus className="h-4 w-4" /> Add Student
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <Input data-testid="students-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name…" className="h-10 rounded-xl pl-9" />
        </div>
        <Select value={course} onValueChange={setCourse}>
          <SelectTrigger data-testid="students-course-filter" className="h-10 w-full rounded-xl sm:w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            {courses.map((c) => <SelectItem key={c} value={c}>{c === "All" ? "All courses" : c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {rows.map((s) => {
          const rc = riskStyle(s.risk_level);
          return (
            <div key={s.id} data-testid={`student-card-${s.id}`}
              className="rounded-2xl border border-border bg-white p-5 card-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-sm font-bold text-ink">{initials(s.name)}</span>
                  <div>
                    <div className="font-bold text-ink">{s.name}</div>
                    <div className="text-xs text-ink-muted">{s.grade} · {s.course}</div>
                  </div>
                </div>
                <span className="font-display text-2xl font-bold" style={{ color: rc.color }}>{s.risk_score}</span>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <RiskBadge level={s.risk_level} size="sm" />
                <span className="text-sm font-semibold text-ink">{formatINR(s.monthly_fee)}/mo</span>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-secondary/60 py-2">
                  <div className="text-xs text-ink-muted">Attend.</div>
                  <div className="text-sm font-bold text-ink">{s.attendance_pct}%</div>
                </div>
                <div className="rounded-lg bg-secondary/60 py-2">
                  <div className="text-xs text-ink-muted">Score</div>
                  <div className="text-sm font-bold text-ink">{s.latest_score}%</div>
                </div>
                <div className="rounded-lg bg-secondary/60 py-2">
                  <div className="text-xs text-ink-muted">Homework</div>
                  <div className="text-sm font-bold text-ink">{s.homework_pct}%</div>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditing(s)}
                  data-testid={`edit-${s.id}`} className="flex-1 gap-1 rounded-lg">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
                <Button size="sm" onClick={() => nav(`/students/${s.id}`)}
                  data-testid={`profile-${s.id}`} className="flex-1 gap-1 rounded-lg bg-ink hover:bg-ink-soft">
                  Profile <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      {rows.length === 0 && <div className="py-12 text-center text-ink-muted">No students found.</div>}

      <AddStudentDialog open={addOpen} onOpenChange={setAddOpen}
        onCreated={(s) => nav(`/students/${s.id}`)} />
      {editing && (
        <AddStudentDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}
          initial={editing} isEdit onCreated={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}
